import { useState, useEffect, useRef } from 'react';
import { useBusinessUnit } from '@/App';
import { agents as agentsApi } from '@/lib/api';
import { formatCurrency, formatPercent } from '@/lib/utils';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, Search, Trash2, Edit2, Users, Mail, Phone, MapPin, CheckCircle, XCircle, Home, Briefcase, Copy, Link2, ExternalLink, Eye, EyeOff, FolderOpen, Camera, Upload, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Agents() {
  const { businessUnit } = useBusinessUnit();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const photoInputRef = useRef(null);
  
  // Business unit labels
  const buLabel = businessUnit === 'insurance' ? 'Insurance & Investment' : 'Mortgage';
  
  // Get agent signup link
  const getAgentSignupLink = () => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/agent/signup?division=${businessUnit}`;
  };

  const copyAgentSignupLink = () => {
    const link = getAgentSignupLink();
    
    // Try modern clipboard API first
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(link).then(() => {
        toast.success('Agent signup link copied to clipboard!');
      }).catch(() => {
        fallbackCopy(link);
      });
    } else {
      fallbackCopy(link);
    }
  };

  const fallbackCopy = (text) => {
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      textArea.style.top = '-9999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        toast.success('Agent signup link copied to clipboard!');
      } else {
        toast.error('Failed to copy link. Please copy manually.');
      }
    } catch (err) {
      toast.error('Failed to copy link. Please copy manually.');
    }
  };
  
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    email: '',
    phone: '',
    split_percentage: '0.85',
    status: 'active',
    sin: '',
    employment_type: 'contractor',
    common_folder_url: '',
    clients_folder_url: '',
    photo_url: ''
  });

  // State for SIN visibility toggle
  const [showSin, setShowSin] = useState({});

  useEffect(() => {
    loadAgents();
  }, [businessUnit]); // Reload when business unit changes

  const loadAgents = async () => {
    setLoading(true);
    try {
      const response = await agentsApi.list();
      setAgents(response.data);
    } catch (err) {
      toast.error('Failed to load agents');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      address: '',
      email: '',
      phone: '',
      split_percentage: '0.85',
      status: 'active',
      sin: '',
      employment_type: 'contractor',
      common_folder_url: '',
      clients_folder_url: '',
      photo_url: ''
    });
    setEditingId(null);
    setPhotoPreview(null);
  };

  const openEdit = (agent) => {
    setFormData({
      name: agent.name,
      address: agent.address || '',
      email: agent.email || '',
      phone: agent.phone || '',
      split_percentage: agent.split_percentage?.toString() || '0.85',
      status: agent.status || 'active',
      sin: agent.sin || '',
      employment_type: agent.employment_type || 'contractor',
      common_folder_url: agent.common_folder_url || '',
      clients_folder_url: agent.clients_folder_url || '',
      photo_url: agent.photo_url || ''
    });
    setEditingId(agent.id);
    setPhotoPreview(agent.photo_url || null);
    setShowModal(true);
  };

  // Handle photo file selection
  const handlePhotoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }
    
    // If editing existing agent, upload immediately
    if (editingId) {
      setUploadingPhoto(true);
      try {
        const token = localStorage.getItem('token');
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        
        const response = await axios.post(
          `${API}/api/agents/${editingId}/photo`,
          formDataUpload,
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
        );
        
        setPhotoPreview(response.data.photo_url);
        setFormData(prev => ({ ...prev, photo_url: response.data.photo_url }));
        toast.success('Photo uploaded successfully');
        loadAgents();
      } catch (err) {
        toast.error('Failed to upload photo');
      } finally {
        setUploadingPhoto(false);
      }
    } else {
      // For new agent, just preview the image
      const reader = new FileReader();
      reader.onload = (event) => {
        setPhotoPreview(event.target.result);
        setFormData(prev => ({ ...prev, photo_url: event.target.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle photo removal
  const handleRemovePhoto = async () => {
    if (editingId) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${API}/api/agents/${editingId}/photo`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Photo removed');
        loadAgents();
      } catch (err) {
        toast.error('Failed to remove photo');
      }
    }
    setPhotoPreview(null);
    setFormData(prev => ({ ...prev, photo_url: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        split_percentage: parseFloat(formData.split_percentage) || 0.85
      };

      if (editingId) {
        await agentsApi.update(editingId, payload);
        toast.success('Agent updated');
      } else {
        await agentsApi.create(payload);
        toast.success('Agent created');
      }
      setShowModal(false);
      resetForm();
      loadAgents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Operation failed');
    }
  };

  const handleToggleStatus = async (agent) => {
    try {
      const newStatus = agent.status === 'active' ? 'inactive' : 'active';
      await agentsApi.update(agent.id, {
        ...agent,
        status: newStatus
      });
      toast.success(`Agent ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
      loadAgents();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this agent?')) return;
    try {
      await agentsApi.delete(id);
      toast.success('Agent deleted');
      loadAgents();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const filteredAgents = agents.filter(a => {
    const matchesSearch = a.name?.toLowerCase().includes(search.toLowerCase()) ||
      a.email?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || a.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const activeCount = agents.filter(a => a.status === 'active').length;
  const inactiveCount = agents.filter(a => a.status === 'inactive').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="agents-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>
            Agents
          </h1>
          <p className="text-slate-500 mt-1">Manage {buLabel.toLowerCase()} agents</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Agent Signup Link */}
          <div className="flex items-center gap-2 px-3 py-2 bg-slate-100 rounded-lg border text-sm">
            <Link2 className="w-4 h-4 text-slate-500" />
            <span className="text-slate-600 font-mono max-w-[150px] truncate hidden sm:inline">
              {getAgentSignupLink()}
            </span>
          </div>
          <Button
            onClick={copyAgentSignupLink}
            variant="outline"
            data-testid="copy-agent-link-btn"
          >
            <Copy className="w-4 h-4 mr-2" />
            Copy Link
          </Button>
          <Button
            variant="outline"
            onClick={() => window.open(getAgentSignupLink(), '_blank')}
            data-testid="open-agent-link-btn"
          >
            <ExternalLink className="w-4 h-4" />
          </Button>
          <Button
            onClick={() => { resetForm(); setShowModal(true); }}
            data-testid="add-agent-btn"
            className="bg-slate-900 hover:bg-slate-800"
          >
            <Plus className="w-4 h-4 mr-2" /> Add Agent
          </Button>
        </div>
      </div>

      {/* Stats & Search */}
      <div className="flex flex-col sm:flex-row gap-4">
        <Card className="flex-shrink-0 border-slate-100">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-violet-50 flex items-center justify-center">
              <Users className="w-6 h-6 text-violet-500" />
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider">Total Agents</p>
              <p className="text-2xl font-bold text-slate-900">{agents.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="flex-shrink-0 border-slate-100">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-emerald-500" />
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider">Active</p>
              <p className="text-2xl font-bold text-emerald-600">{activeCount}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="flex-shrink-0 border-slate-100">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
              <XCircle className="w-6 h-6 text-slate-400" />
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider">Inactive</p>
              <p className="text-2xl font-bold text-slate-400">{inactiveCount}</p>
            </div>
          </CardContent>
        </Card>
        <div className="flex-1 flex gap-2">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search agents..."
              data-testid="search-agents"
              className="pl-10"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[130px]" data-testid="status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Agents Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredAgents.map((agent) => (
          <Card
            key={agent.id}
            className={`border-slate-100 shadow-sm hover:shadow-md transition-shadow ${agent.status === 'inactive' ? 'opacity-60' : ''}`}
            data-testid={`agent-card-${agent.id}`}
          >
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg ${agent.status === 'active' ? 'bg-slate-900' : 'bg-slate-400'}`}>
                    {agent.name?.charAt(0) || 'A'}
                  </div>
                  <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                    agent.status === 'active' 
                      ? 'bg-emerald-100 text-emerald-700' 
                      : 'bg-slate-100 text-slate-500'
                  }`}>
                    {agent.status === 'active' ? 'Active' : 'Inactive'}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEdit(agent)}
                    data-testid={`edit-agent-${agent.id}`}
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(agent.id)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    data-testid={`delete-agent-${agent.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Agent Photo and Name */}
              <div className="flex items-center gap-3 mb-3">
                {agent.photo_url ? (
                  <img 
                    src={agent.photo_url} 
                    alt={agent.name}
                    className="w-12 h-12 rounded-full object-cover border-2 border-slate-200"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-100 to-emerald-200 flex items-center justify-center">
                    <span className="text-emerald-700 font-bold text-lg">
                      {agent.name?.charAt(0)?.toUpperCase()}
                    </span>
                  </div>
                )}
                <h3 className="font-bold text-slate-900 text-lg flex-1" style={{ fontFamily: 'Manrope' }}>
                  {agent.name}
                </h3>
              </div>

              <div className="space-y-2 text-sm text-slate-600">
                {agent.email && (
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-slate-400" />
                    <span className="truncate">{agent.email}</span>
                  </div>
                )}
                {agent.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <span>{agent.phone}</span>
                  </div>
                )}
                {agent.address && (
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-slate-400 mt-0.5" />
                    <span className="text-xs line-clamp-2">{agent.address}</span>
                  </div>
                )}
                {/* Employment Type & SIN */}
                <div className="flex items-center gap-2">
                  <Briefcase className="w-4 h-4 text-slate-400" />
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${agent.employment_type === 'employee' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                    {agent.employment_type === 'employee' ? 'Employee (T4)' : 'Contractor (T4A)'}
                  </span>
                </div>
                {agent.sin_masked && (
                  <div className="flex items-center gap-2">
                    <span className="text-slate-400 text-xs">SIN:</span>
                    <span className="font-mono text-xs">
                      {showSin[agent.id] ? agent.sin : agent.sin_masked}
                    </span>
                    <button 
                      onClick={() => setShowSin(prev => ({...prev, [agent.id]: !prev[agent.id]}))}
                      className="text-blue-500 text-xs hover:underline"
                    >
                      {showSin[agent.id] ? 'Hide' : 'Show'}
                    </button>
                  </div>
                )}
              </div>

              <div className="mt-4 pt-4 border-t border-slate-100">
                <div className="flex justify-between items-center mb-3">
                  <div>
                    <p className="text-xs text-slate-500">Split %</p>
                    <p className="font-mono font-medium text-slate-900">{formatPercent(agent.split_percentage)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-500">Total Earned</p>
                    <p className="font-mono font-medium text-emerald-600">{formatCurrency(agent.total_commissions)}</p>
                  </div>
                </div>
                
                {/* Status Toggle */}
                <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                  <span className="text-sm text-slate-600">Status</span>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs ${agent.status === 'active' ? 'text-emerald-600' : 'text-slate-400'}`}>
                      {agent.status === 'active' ? 'Active' : 'Inactive'}
                    </span>
                    <Switch
                      checked={agent.status === 'active'}
                      onCheckedChange={() => handleToggleStatus(agent)}
                      data-testid={`toggle-status-${agent.id}`}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredAgents.length === 0 && (
        <div className="text-center py-12 text-slate-500">
          No agents found
        </div>
      )}

      {/* Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent data-testid="agent-modal">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>
              {editingId ? 'Edit Agent' : 'Add Agent'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Photo Upload Section */}
            <div className="flex flex-col items-center gap-3 pb-4 border-b border-slate-200">
              <div className="relative">
                {photoPreview ? (
                  <div className="relative">
                    <img 
                      src={photoPreview} 
                      alt="Agent" 
                      className="w-24 h-24 rounded-full object-cover border-4 border-emerald-100"
                    />
                    <button
                      type="button"
                      onClick={handleRemovePhoto}
                      className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                      title="Remove photo"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="w-24 h-24 rounded-full bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center">
                    <Camera className="w-8 h-8 text-slate-400" />
                  </div>
                )}
              </div>
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoSelect}
                className="hidden"
                data-testid="agent-photo-input"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => photoInputRef.current?.click()}
                disabled={uploadingPhoto}
                className="text-xs"
              >
                <Upload className="w-3 h-3 mr-1" />
                {uploadingPhoto ? 'Uploading...' : photoPreview ? 'Change Photo' : 'Upload Photo'}
              </Button>
              <p className="text-xs text-slate-500">Max 5MB, JPG/PNG/GIF</p>
            </div>

            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="John Doe"
                required
                data-testid="agent-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="agent@example.com"
              />
            </div>

            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="(204) 555-0123"
              />
            </div>

            <div className="space-y-2">
              <Label>Address</Label>
              <Textarea
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="123 Main St, City, Province"
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Default Split % (decimal, e.g. 0.85 = 85%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="1"
                  value={formData.split_percentage}
                  onChange={(e) => setFormData({ ...formData, split_percentage: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(v) => setFormData({ ...formData, status: v })}
                >
                  <SelectTrigger data-testid="agent-status-select">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Employment Type */}
            <div className="space-y-2">
              <Label>Employment Type</Label>
              <Select
                value={formData.employment_type}
                onValueChange={(v) => setFormData({ ...formData, employment_type: v })}
              >
                <SelectTrigger data-testid="agent-employment-type-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="contractor">Contractor (T4A)</SelectItem>
                  <SelectItem value="employee">Employee (T4)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                Contractors receive T4A slips, Employees receive T4 slips
              </p>
            </div>

            {/* SIN */}
            <div className="space-y-2">
              <Label>Social Insurance Number (SIN)</Label>
              <Input
                value={formData.sin}
                onChange={(e) => setFormData({ ...formData, sin: e.target.value })}
                placeholder="123-456-789"
                data-testid="agent-sin-input"
              />
              <p className="text-xs text-slate-500">
                Required for T4/T4A tax slip generation
              </p>
            </div>

            {/* Folder Links Section */}
            <div className="border-t pt-4 mt-4">
              <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                <FolderOpen className="w-4 h-4" />
                Folder Links
              </h4>
              
              {/* Common Folder URL */}
              <div className="space-y-2 mb-3">
                <Label>Common Folder URL</Label>
                <Input
                  type="url"
                  value={formData.common_folder_url}
                  onChange={(e) => setFormData({ ...formData, common_folder_url: e.target.value })}
                  placeholder="https://drive.google.com/..."
                  data-testid="agent-common-folder-input"
                />
                <p className="text-xs text-slate-500">
                  External link to shared/common folder (e.g., Google Drive, Dropbox)
                </p>
              </div>

              {/* Clients' Folder URL */}
              <div className="space-y-2">
                <Label>Clients' Folder URL</Label>
                <Input
                  type="url"
                  value={formData.clients_folder_url}
                  onChange={(e) => setFormData({ ...formData, clients_folder_url: e.target.value })}
                  placeholder="https://drive.google.com/..."
                  data-testid="agent-clients-folder-input"
                />
                <p className="text-xs text-slate-500">
                  External link to client-specific folder (e.g., Google Drive, Dropbox)
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-agent-btn">
                {editingId ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
